import { combineEpics, ofType } from 'redux-observable'
import { forkJoin, of } from 'rxjs'
import { catchError, debounceTime, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { getRangeFromDateFilter, getTimezoneId, getTZOffset } from 'utils/dateUtils'

import { addToast } from 'containers/Toasts/actions'

import {
  GET_DEEPFAKE_ANALYTICS_ASYNC,
  GET_DEEPFAKE_ANALYTICS_PREVIOUS_ASYNC,
  GET_DEEPFAKE_CALL_DETAILS_ASYNC,
  GET_DEEPFAKE_MEETINGS_ASYNC,
  markDeepfakeFailure,
  markDeepfakeSuccess,
  markNotDeepfakeFailure,
  markNotDeepfakeSuccess,
} from './meetingsActions'
import {
  CLEAR_DEEPFAKE_MEETINGS_FILTERS,
  SET_DEEPFAKE_MEETINGS_FILTERS,
  SET_DEEPFAKE_MEETINGS_LIVE_ONLY,
  SET_DEEPFAKE_MEETINGS_ORDERING,
  SET_DEEPFAKE_MEETINGS_PAGE,
  SET_DEEPFAKE_MEETINGS_PAGE_SIZE,
  SET_DEEPFAKE_MEETINGS_TIME_FILTER,
} from './meetingsConstants'

const ANALYTICS_ERROR_MESSAGE = 'Failed to load analytics. Please try again.'
const MARK_NOT_DEEPFAKE_ERROR_MESSAGE = 'Failed to mark as not deepfake. Please try again.'
const MARK_DEEPFAKE_ERROR_MESSAGE = 'Failed to mark as deepfake. Please try again.'
const MISSING_CALL_PARTICIPANT_ID_ERROR = 'Missing callParticipantId'

// Helper to calculate previous period date range
const calculatePreviousPeriod = (currentDateRange) => {
  if (!currentDateRange || !Array.isArray(currentDateRange) || currentDateRange.length !== 2) {
    return null
  }

  const startDate = new Date(currentDateRange[0])
  const endDate = new Date(currentDateRange[1])

  // Calculate the duration of the current period
  const duration = endDate.getTime() - startDate.getTime()

  // Calculate previous period: shift back by the duration
  const previousEndDate = new Date(startDate.getTime() - 1) // One millisecond before current start
  const previousStartDate = new Date(previousEndDate.getTime() - duration)

  return [previousStartDate.toISOString(), previousEndDate.toISOString()]
}

// Helper to build query params from meetings filters
const buildDeepfakeMeetingsParams = (filters = {}, page = 1, pageSize = 50) => {
  const params = {}

  // Page and page size
  params.page = page
  params.pageSize = pageSize

  // Search by name/subject
  if (filters.searchSubject?.trim()) {
    params.search = filters.searchSubject.trim()
  }

  // Status filter - support multiple values.
  // Suspicious badge (callsWithDeepfakeCount) includes confirmed meetings;
  // keep the Suspicious list filter aligned with that count (IC-50267).
  if (filters.statusFilter && filters.statusFilter !== 'all') {
    params.status =
      filters.statusFilter === 'suspicious' ? ['suspicious', 'confirmed'] : filters.statusFilter
  }

  // Call type - based on liveOnly filter
  if (filters.liveOnly) {
    params.call_type = 'live'
  } else {
    params.call_type = 'all'
  }

  // Meeting attendees - support multiple values
  if (filters.attendeeFilter && filters.attendeeFilter.length > 0) {
    params.meetingAttendees = filters.attendeeFilter
  }

  // Ordering
  if (filters.ordering) {
    // Use ordering as-is
    params.order_by = filters.ordering
  }
  // Time filter - add startTime__gte and startTime__lte parameters
  if (filters.timeFilter) {
    if (Array.isArray(filters.timeFilter) && filters.timeFilter.length === 2) {
      // Custom date range - format dates for meetings API with specific times
      const startDate = new Date(filters.timeFilter[0])
      const endDate = new Date(filters.timeFilter[1])

      // Set end time to 23:59:59, keep start time as is
      endDate.setUTCHours(23, 59, 59, 999)

      // Format as YYYY-MM-DD HH:mm:ss (with space instead of T to avoid URL encoding)
      params.callStartTimeGte = `${startDate.toISOString().slice(0, 10)} ${startDate
        .toISOString()
        .slice(11, 19)}`
      params.callStartTimeLte = `${endDate.toISOString().slice(0, 10)} ${endDate
        .toISOString()
        .slice(11, 19)}`
    } else {
      // Predefined time ranges - convert to date range and format
      const dateRange = getRangeFromDateFilter(filters.timeFilter)
      if (dateRange && dateRange.length === 2) {
        const startDate = new Date(dateRange[0])
        const endDate = new Date(dateRange[1])

        // Set end time to 23:59:59, keep start time as is
        endDate.setUTCHours(23, 59, 59, 999)

        // Format as YYYY-MM-DD HH:mm:ss (with space instead of T to avoid URL encoding)
        params.callStartTimeGte = `${startDate.toISOString().slice(0, 10)} ${startDate
          .toISOString()
          .slice(11, 19)}`
        params.callStartTimeLte = `${endDate.toISOString().slice(0, 10)} ${endDate
          .toISOString()
          .slice(11, 19)}`
      }
    }
  }

  return params
}

const getDeepfakeMeetingsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_DEEPFAKE_MEETINGS_ASYNC.INITIATE,
      SET_DEEPFAKE_MEETINGS_FILTERS,
      SET_DEEPFAKE_MEETINGS_LIVE_ONLY,
      SET_DEEPFAKE_MEETINGS_TIME_FILTER,
      SET_DEEPFAKE_MEETINGS_ORDERING,
      SET_DEEPFAKE_MEETINGS_PAGE,
      SET_DEEPFAKE_MEETINGS_PAGE_SIZE,
      CLEAR_DEEPFAKE_MEETINGS_FILTERS
    ),
    debounceTime(100),
    switchMap(() => {
      const state = state$.value
      const meetingsState = state.deepfake?.meetings
      const errorMessage = 'Deepfake meetings state not found'

      if (!meetingsState) {
        return of(GET_DEEPFAKE_MEETINGS_ASYNC.onError(errorMessage))
      }

      const { filters, data } = meetingsState
      const { page, pageSize } = data

      const params = buildDeepfakeMeetingsParams(filters, page, pageSize)

      return api.settings.getDeepfakeMeetings(params).pipe(
        map(GET_DEEPFAKE_MEETINGS_ASYNC.onSuccess),
        catchError((error) => {
          const actions = [epicsErrorHandler(GET_DEEPFAKE_MEETINGS_ASYNC.onError, error)]

          const toastErrorMessage = 'Failed to load meetings. Please try again.'

          actions.push(
            addToast({
              message: toastErrorMessage,
              type: 'error',
            })
          )

          return of(...actions)
        }),
        takeUntil(action$.pipe(ofType(GET_DEEPFAKE_MEETINGS_ASYNC.CANCEL)))
      )
    })
  )

const getDeepfakeAnalyticsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_DEEPFAKE_ANALYTICS_ASYNC.INITIATE,
      SET_DEEPFAKE_MEETINGS_TIME_FILTER,
      CLEAR_DEEPFAKE_MEETINGS_FILTERS
    ),
    debounceTime(100),
    switchMap(() => {
      const state = state$.value
      const meetingsState = state.deepfake?.meetings

      if (!meetingsState) {
        return of(GET_DEEPFAKE_ANALYTICS_ASYNC.onError('Meetings state not found'))
      }

      const { timeFilter } = meetingsState.filters
      const companyFilter = state.partnerOptions?.companyFilter?.value || ''

      let feDateRange
      if (Array.isArray(timeFilter) && timeFilter.length === 2) {
        feDateRange = timeFilter
      } else {
        feDateRange = getRangeFromDateFilter(timeFilter)
      }

      if (!feDateRange || !Array.isArray(feDateRange) || feDateRange.length !== 2) {
        return of(GET_DEEPFAKE_ANALYTICS_ASYNC.onError('Invalid date range for analytics'))
      }

      // Calculate previous period
      const previousDateRange = calculatePreviousPeriod(feDateRange)

      // Prepare current period request
      const currentBody = {
        fe_date_range: feDateRange,
        offset: getTZOffset(),
        timezone: getTimezoneId(),
      }

      const currentRequest = api.dashboard.getDeepfakeAnalytic(companyFilter, currentBody).pipe(
        map(({ response }) => response),
        catchError((error) => of(null))
      )

      // Prepare previous period request if we have a valid previous range
      const previousRequest = previousDateRange
        ? api.dashboard
            .getDeepfakeAnalytic(companyFilter, {
              fe_date_range: previousDateRange,
              offset: getTZOffset(),
              timezone: getTimezoneId(),
            })
            .pipe(
              map(({ response }) => response),
              catchError((error) => of(null))
            )
        : of(null)

      // Execute both requests in parallel
      return forkJoin([currentRequest, previousRequest]).pipe(
        mergeMap(([currentResponse, previousResponse]) => {
          const actions = []
          // Dispatch success for current period
          if (currentResponse) {
            actions.push(GET_DEEPFAKE_ANALYTICS_ASYNC.onSuccess(currentResponse))
          } else {
            actions.push(
              addToast({
                message: ANALYTICS_ERROR_MESSAGE,
                type: 'error',
              })
            )
          }
          // Dispatch success for previous period if we have it
          if (previousResponse) {
            actions.push(GET_DEEPFAKE_ANALYTICS_PREVIOUS_ASYNC.onSuccess(previousResponse))
          }
          return of(...actions)
        }),
        catchError((error) => {
          const actions = [epicsErrorHandler(GET_DEEPFAKE_ANALYTICS_ASYNC.onError, error)]

          actions.push(
            addToast({
              message: ANALYTICS_ERROR_MESSAGE,
              type: 'error',
            })
          )

          return of(...actions)
        }),
        takeUntil(action$.pipe(ofType(GET_DEEPFAKE_ANALYTICS_ASYNC.CANCEL)))
      )
    })
  )

const getDeepfakeCallDetailsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_DEEPFAKE_CALL_DETAILS_ASYNC.INITIATE),
    switchMap((action) => {
      const callId = action.payload?.callId
      const params = callId ? { call_id: callId } : {}

      return api.settings.getDeepfakeCalls(params).pipe(
        map(GET_DEEPFAKE_CALL_DETAILS_ASYNC.onSuccess),
        catchError((error) => {
          const actions = [epicsErrorHandler(GET_DEEPFAKE_CALL_DETAILS_ASYNC.onError, error)]
          const toastErrorMessage = 'Failed to load meeting details. Please try again.'

          actions.push(
            addToast({
              message: toastErrorMessage,
              type: 'error',
            })
          )

          return of(...actions)
        }),
        takeUntil(action$.pipe(ofType(GET_DEEPFAKE_CALL_DETAILS_ASYNC.CANCEL)))
      )
    })
  )

const markNotDeepfakeEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType('MARK_NOT_DEEPFAKE_REQUEST'),
    switchMap((action) => {
      const { callParticipantId, callId, reasonText } = action.payload || {}
      if (!callParticipantId) {
        return of(
          markNotDeepfakeFailure(new Error(MISSING_CALL_PARTICIPANT_ID_ERROR)),
          addToast({ message: MARK_NOT_DEEPFAKE_ERROR_MESSAGE, type: 'error' })
        )
      }
      return api.settings
        .markDeepfakeFalsePositive(callParticipantId, 'mark_not_deepfake', reasonText)
        .pipe(
          mergeMap((response) =>
            of(
              markNotDeepfakeSuccess(response),
              GET_DEEPFAKE_CALL_DETAILS_ASYNC.initiate({ callId })
            )
          ),
          catchError((error) =>
            of(
              markNotDeepfakeFailure(error),
              addToast({ message: MARK_NOT_DEEPFAKE_ERROR_MESSAGE, type: 'error' })
            )
          )
        )
    })
  )

const markDeepfakeEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType('MARK_DEEPFAKE_REQUEST'),
    switchMap((action) => {
      const { callParticipantId, callId, reasonText } = action.payload || {}
      if (!callParticipantId) {
        return of(
          markDeepfakeFailure(new Error(MISSING_CALL_PARTICIPANT_ID_ERROR)),
          addToast({ message: MARK_DEEPFAKE_ERROR_MESSAGE, type: 'error' })
        )
      }
      return api.settings
        .markDeepfakeFalsePositive(callParticipantId, 'mark_deepfake', reasonText)
        .pipe(
          mergeMap((response) =>
            of(markDeepfakeSuccess(response), GET_DEEPFAKE_CALL_DETAILS_ASYNC.initiate({ callId }))
          ),
          catchError((error) =>
            of(
              markDeepfakeFailure(error),
              addToast({ message: MARK_DEEPFAKE_ERROR_MESSAGE, type: 'error' })
            )
          )
        )
    })
  )

export default combineEpics(
  getDeepfakeMeetingsEpic,
  getDeepfakeAnalyticsEpic,
  getDeepfakeCallDetailsEpic,
  markNotDeepfakeEpic,
  markDeepfakeEpic
)
